import type { ConfidenceLabel } from "@/lib/types";

export type ConfidenceResult = {
  score: number;
  label: ConfidenceLabel;
  reasons: string[];
};

type ConfidenceInputs = {
  playsLikeYards: number;
  clubCarryYards: number;
  lieType?: string;
  slopeYds?: number;
  energyPct?: number;
  handicap?: number;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function baselineFromHandicap(handicap: number) {
  // Anchor around mid-handicap = 12
  // Lower handicap -> higher baseline confidence
  // Higher handicap -> lower baseline confidence
  const baseline = 72 - (handicap - 12) * 0.8;
  return clamp(Math.round(baseline), 45, 85);
}

export function computeConfidence(inputs: ConfidenceInputs): ConfidenceResult {
  const {
    playsLikeYards,
    clubCarryYards,
    lieType = "fairway",
    slopeYds = 0,
    energyPct = 100,
    handicap = 15,
  } = inputs;

  let score = baselineFromHandicap(handicap);
  const reasons: string[] = [];

  // 1) Distance fit: most important v1 driver
  const gap = Math.abs(clubCarryYards - playsLikeYards);

  if (gap <= 3) {
    score += 12;
    reasons.push("Club carry is an excellent match to plays-like yardage.");
  } else if (gap <= 6) {
    score += 8;
    reasons.push("Club carry is a strong match to plays-like yardage.");
  } else if (gap <= 10) {
    score += 4;
    reasons.push("Club carry is a reasonable match to plays-like yardage.");
  } else if (gap <= 15) {
    score -= 3;
    reasons.push("Club carry fit is less precise.");
  } else if (gap <= 20) {
    score -= 8;
    reasons.push("Club carry fit is weak and lowers confidence.");
  } else {
    score -= 14;
    reasons.push("Club carry is a poor fit for the required adjusted yardage.");
  }

  switch (lieType) {
    case "tee":
      score += 4;
      reasons.push("Tee lie improves strike consistency.");
      break;
    case "fairway":
      score += 2;
      reasons.push("Fairway lie supports clean contact.");
      break;
    case "first-cut":
      score -= 3;
      reasons.push("First cut adds slight uncertainty.");
      break;
    case "light-rough":
      score -= 4;
      reasons.push("Light rough reduces strike predictability.");
      break;
    case "heavy-rough":
      score -= 7;
      reasons.push("Heavy rough materially reduces strike predictability.");
      break;
    case "recovery":
      score -= 10;
      reasons.push("Recovery lie materially lowers confidence.");
      break;
    default:
      break;
  }
  // 3) Slope severity
  // Uphill generally raises carry/execution demand.
  // Downhill can increase descent angle and reduce rollout,
  // so it is not penalized identically to uphill.
  const slopeAbs = Math.abs(slopeYds);

  if (slopeAbs <= 3) {
    score += 2;
  } else if (slopeAbs <= 7) {
    // no change
  } else if (slopeAbs <= 12) {
    if (slopeYds >= 0) {
      score -= 4;
      reasons.push("Moderate uphill adjustment increases carry and execution demand.");
    } else {
      score -= 2;
      reasons.push("Moderate downhill adjustment changes landing and rollout behavior.");
    }
  } else if (slopeAbs <= 18) {
    if (slopeYds >= 0) {
      score -= 8;
      reasons.push("Steep uphill adjustment lowers confidence.");
    } else {
      score -= 5;
      reasons.push("Steep downhill adjustment increases carry-control sensitivity.");
    }
  } else {
    if (slopeYds >= 0) {
      score -= 12;
      reasons.push("Severe uphill adjustment materially lowers confidence.");
    } else {
      score -= 8;
      reasons.push("Severe downhill adjustment materially changes landing dynamics.");
    }
  }

  // 4) Energy / fatigue
  if (energyPct >= 90) {
    score += 2;
  } else if (energyPct >= 75) {
    // no change
  } else if (energyPct >= 60) {
    score -= 4;
    reasons.push("Reduced energy may affect execution.");
  } else if (energyPct >= 40) {
    score -= 8;
    reasons.push("Fatigue lowers consistency.");
  } else {
    score -= 14;
    reasons.push("Significant fatigue materially lowers confidence.");
  }

  score = clamp(Math.round(score), 0, 100);

  let label: ConfidenceLabel = "Medium";
  if (score >= 80) label = "High";
  else if (score < 60) label = "Low";

  return { score, label, reasons };
}